/**
 * Review eines Fragebogens — Pipeline-Stufe 4 (Konzept §3).
 *
 * Die Seite, auf der das Versprechen des Features eingelöst wird: „Das System
 * produziert VORSCHLÄGE mit Konfidenz + Quelle; ein QM-Mensch reviewt jede
 * Antwort vor dem Schreiben ins Dokument" (Konzept §2.2).
 *
 * Aufbau (F6-Sanierung): eine **App-Shell** — Kopf (Fortschritt + Sammel-
 * aktionen + Reiter), Abschnitts-TOC rechts und die Abschluss-Leiste bleiben
 * frozen; NUR die Fragenliste scrollt und stoppt an ihrem Ende (Befund B6).
 *
 * 1. **Fortschritt + Sammelaktionen** — wie viel Arbeit liegt vor mir; die
 *    sicheren Fälle in einem Zug übernehmen, den Rest „abhaken" (B8).
 * 2. **Fragen nach Sektion** als verdichtete Zeilenliste; **Entscheidungen
 *    inline in ihrer Zeile** (B1: kein Stapel mehr oben), ausgeklammerte
 *    Sektionen eingeklappt mit Begründung und umkehrbar.
 * 3. **Abschnitts-TOC rechts** als frozen Spalte (B4), Chip-Leiste auf Mobile.
 * 4. **Lücken** und **Anlagen** als eigene Reiter.
 *
 * Nach jeder Änderung wird die Fragenliste neu geladen. Das ist Absicht: eine
 * Entscheidung ändert fremde Zeilen (Folgefragen-Gating), und ein lokal
 * fortgeschriebener Zustand würde genau dort auseinanderlaufen, wo es
 * fachlich weh tut.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCheck,
  FileText,
  Info,
  ListChecks,
  Loader2,
  Package,
  Stamp,
} from "lucide-react";
import clsx from "clsx";

import { useT, type Katalog } from "../i18n";
// Nach einem `await` gilt der Katalog von JETZT, nicht der vom Render-Zeitpunkt
// (docs/i18n.md §3) — deshalb `katalog()` in den Meldungen der Mutationen.
import { katalog } from "../i18n/lang";
import { Button, Card, Chip, EmptyState, StatusBadge, Tabs } from "../ui";
import { useToast } from "../ui/toast";
import {
  fragebogenApi,
  ladeBlobUrl,
  ladeDatei,
  paketPfad,
  vorschauPfad,
  type FinaleAntwort,
  type Frage,
  type FragebogenDetail,
  type FragenAntwort,
  type Fortschritt,
  type KontaktFeld,
  type KontaktVerzeichnisEintrag,
  type OffeneStelle,
  type ReviewState,
} from "./api";
import AnlagenCheckliste, { anlagenFragen } from "./Anlagen";
import FrageKarte, {
  type EntscheidungDeko,
  type EntscheidungWahl,
} from "./FrageKarte";
import { ankerArt, anzeigeNummer, laufBeschriftung, statusInfo } from "./labels";

type Reiter = "review" | "luecken" | "anlagen" | "dokument";

export default function FragebogenReview() {
  const { fragebogenId = "" } = useParams();
  const [kopf, setKopf] = useState<FragebogenDetail | null>(null);
  const [daten, setDaten] = useState<FragenAntwort | null>(null);
  const [reiter, setReiter] = useState<Reiter>("review");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // „Dokument"-Reiter: die gefüllte Datei als PDF-Blob-URL für das <iframe>.
  const [vorschauUrl, setVorschauUrl] = useState<string | null>(null);
  const [vorschauLaedt, setVorschauLaedt] = useState(false);
  const [vorschauFehler, setVorschauFehler] = useState<string | null>(null);
  const toast = useToast();
  const t = useT().questionnaires;
  // B2: das Mitarbeiter-Verzeichnis für die Kontakt-Entscheidungen — einmal
  // geladen, allen Inline-Editoren gereicht.
  const [verzeichnis, setVerzeichnis] = useState<KontaktVerzeichnisEintrag[]>([]);
  // B1: geteilte Kontaktwerte je Rolle (2× ceo). Als Ref, damit ein Tastendruck
  // in einem Kontaktfeld nicht die ganze 88-Zeilen-Liste neu rendert.
  // B6: der EIGENE Scroller der Fragenliste (App-Shell) — Header/TOC/Fußleiste
  // bleiben frozen, nur dieser Bereich scrollt. Treibt auch den Scroll-Spy der TOC.
  const listeRef = useRef<HTMLDivElement>(null);
  const rollenWerteRef = useRef<Record<string, Partial<Record<KontaktFeld, string>>>>({});
  const setRollenWerte = useCallback(
    (rolle: string, werte: Partial<Record<KontaktFeld, string>>) => {
      rollenWerteRef.current[rolle] = werte;
    },
    []
  );

  const ladeFragen = useCallback(async () => {
    setDaten(await fragebogenApi.fragen(fragebogenId));
  }, [fragebogenId]);

  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      try {
        const detail = await fragebogenApi.detail(fragebogenId);
        if (abgebrochen) return;
        setKopf(detail);
        // B2: Verzeichnis fail-soft — fällt es aus, funktionieren die
        // Kontakt-Entscheidungen weiter, nur ohne Dropdown.
        fragebogenApi
          .kontaktVerzeichnis()
          .then((v) => !abgebrochen && setVerzeichnis(v))
          .catch(() => undefined);
        // Nur noch die Fragen: der Anlagen-Befund haengt seit K6 an der Frage
        // (`suggestion.anlage`, vom Server gerechnet). Eine zweite Liste zu
        // laden hiesse, im Browser eine zweite Wahrheit vorzuhalten.
        await ladeFragen();
      } catch (e) {
        if (!abgebrochen) setFehler((e as Error).message);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [fragebogenId, ladeFragen]);

  /**
   * Läuft der Bogen noch, wird der Kopf nachgeladen — die Stufe und der
   * Matching-Zähler ändern sich, während der Nutzer hinsieht.
   *
   * Die Abhängigkeit ist bewusst `laeuft` und nicht `kopf`: jeder Tick setzt
   * einen neuen Kopf, und mit `kopf` in der Liste würde das Intervall bei jedem
   * Tick abgeräumt und neu gesetzt. 3 s, weil das Matching alle fünf Fragen
   * einen Stand meldet (`matching.FORTSCHRITT_TAKT`).
   */
  const laeuft = kopf !== null && statusInfo(kopf.status).laeuft;
  useEffect(() => {
    if (!laeuft) return;
    const timer = setInterval(() => {
      fragebogenApi
        .detail(fragebogenId)
        .then((detail) => {
          setKopf(detail);
          // Fertig: jetzt gibt es Fragen zu holen.
          if (!statusInfo(detail.status).laeuft) void ladeFragen();
        })
        .catch(() => undefined); // ein verpasster Tick ist kein Fehler
    }, 3000);
    return () => clearInterval(timer);
  }, [fragebogenId, laeuft, ladeFragen]);

  /** Jede Mutation läuft durch hier: sperren, ausführen, neu laden, melden.
   *  `fehlerTitel` ist ein Katalog-Wähler und kein zusammengesetzter Satz —
   *  „Speichern fehlgeschlagen" lässt sich nicht aus zwei Bausteinen bauen,
   *  ohne im Englischen falsch zu werden. */
  const mutiere = async (
    fehlerTitel: (texte: Katalog["questionnaires"]) => string,
    fn: () => Promise<unknown>
  ) => {
    setBusy(true);
    try {
      await fn();
      await ladeFragen();
    } catch (e) {
      toast.err(fehlerTitel(katalog().questionnaires), (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const setzen = (frage: Frage, state: ReviewState, antwort?: FinaleAntwort | null) =>
    mutiere(
      (tt) => tt.speichernFehlgeschlagen,
      () =>
        fragebogenApi.frageSetzen(fragebogenId, frage.id, {
          review_state: state,
          final_answer: antwort ?? null,
        })
    );

  const neuSuchen = (frage: Frage) =>
    mutiere(
      (tt) => tt.neuSuchenFehlgeschlagen,
      () => fragebogenApi.neuSuchen(fragebogenId, frage.id)
    );

  const sektionSetzen = (section: string | null, applicable: boolean) =>
    mutiere(
      (tt) => tt.sektionFehlgeschlagen,
      () => fragebogenApi.sektionSetzen(fragebogenId, section, applicable)
    );

  const sichereUebernehmen = () =>
    mutiere(
      (tt) => tt.uebernehmenFehlgeschlagen,
      async () => {
        const r = await fragebogenApi.sichereUebernehmen(fragebogenId, ["high"]);
        const tt = katalog().questionnaires;
        toast.ok(tt.vorschlaegeUebernommen(r.uebernommen), tt.nurKonfidenzSicher);
      }
    );

  /** B8: „Alles Übrige abhaken" — offene Vorschläge jeder Konfidenz → geprüft,
   *  restliche offene Fragen → ausgelassen; Entscheidungen/Lücken bleiben. */
  const alleAbhaken = () =>
    mutiere(
      (tt) => tt.abhakenFehlgeschlagen,
      async () => {
        const r = await fragebogenApi.alleAbhaken(fragebogenId);
        const tt = katalog().questionnaires;
        toast.ok(tt.allesAbgehakt(r.uebernommen, r.uebersprungen));
      }
    );

  /** `wahl` trägt entweder den Freitext oder die bestätigten Kontaktfelder —
   *  zusammengesetzt wird die Antwort auf dem Server (er kennt den Anker).
   *  Ohne Notiz-Feld (B1): der Server nimmt `notiz` weiter an, die UI schickt
   *  keine mehr. */
  const entscheiden = (
    frage: Frage,
    wahl: EntscheidungWahl,
    folgefragenGelten: boolean
  ) =>
    mutiere(
      (tt) => tt.entscheidungFehlgeschlagen,
      async () => {
        const r = await fragebogenApi.entscheiden(fragebogenId, frage.id, {
          ...wahl,
          folgefragen_gelten: folgefragenGelten,
          notiz: null,
        });
        const tt = katalog().questionnaires;
        toast.ok(
          tt.entscheidungGespeichert,
          r.folgefragen.length
            ? tt.folgefragenGesetzt(r.folgefragen.length, r.folgefragen_gelten)
            : tt.ohneFolgefragen
        );
      }
    );

  /** Die gefüllte Datei als PDF-Blob laden und im „Dokument"-Reiter anzeigen.
   *  Der Cleanup-Effekt gibt eine ersetzte Blob-URL frei. */
  const ladeVorschau = useCallback(async () => {
    setVorschauLaedt(true);
    setVorschauFehler(null);
    try {
      const r = await ladeBlobUrl(vorschauPfad(fragebogenId));
      if (r.ok && r.url) {
        setVorschauUrl(r.url);
      } else {
        const tt = katalog().questionnaires;
        setVorschauFehler(
          r.status === 404 ? tt.dokumentNochNicht : (r.text ?? "").slice(0, 200)
        );
      }
    } catch (e) {
      setVorschauFehler((e as Error).message);
    } finally {
      setVorschauLaedt(false);
    }
  }, [fragebogenId]);

  // Die Blob-URL freigeben, wenn sie ersetzt wird oder die Seite verschwindet.
  useEffect(() => {
    return () => {
      if (vorschauUrl) URL.revokeObjectURL(vorschauUrl);
    };
  }, [vorschauUrl]);

  /** „Dokument ausfüllen": Antworten ins Original schreiben (P3/P4) und das
   *  Ergebnis im „Dokument"-Reiter ANZEIGEN (statt herunterzuladen). */
  const ausfuellen = async () => {
    if (!kopf) return;
    setBusy(true);
    try {
      const bericht = await fragebogenApi.ausfuellen(fragebogenId);
      const tt = katalog().questionnaires;
      const rest = (bericht.uebersprungen ?? 0) + (bericht.fehler ?? 0);
      toast.ok(
        tt.dokumentGefuellt(bericht.geschrieben ?? 0),
        rest > 0 ? tt.dokumentGefuelltRest(rest) : undefined
      );
      await ladeFragen();
      // Kopf neu laden, damit der Status auf „filled" steht.
      fragebogenApi
        .detail(fragebogenId)
        .then(setKopf)
        .catch(() => undefined);
      setReiter("dokument");
      await ladeVorschau();
    } catch (e) {
      // Der Server erklärt 409 selbst ausführlich („Es stehen noch
      // Entscheidungen aus …") — diese Meldung dem Nutzer zeigen.
      const msg = (e as Error).message;
      const tt = katalog().questionnaires;
      const fehlt = /404|not found|nicht gefunden/i.test(msg);
      toast.err(
        fehlt ? tt.ausfuellenFehlt : tt.ausfuellenFehlgeschlagen,
        fehlt ? tt.ausfuellenFehltDetail : msg
      );
    } finally {
      setBusy(false);
    }
  };

  /** „Download (Paket)": ZIP aus gefülltem Dokument + Lauf-Anlagen. */
  const paketHerunterladen = async () => {
    if (!kopf) return;
    const name =
      kopf.filename.replace(/\.[^.]+$/, "") + katalog().questionnaires.paketDatei;
    const r = await ladeDatei(paketPfad(fragebogenId), name);
    if (!r.ok) {
      const tt = katalog().questionnaires;
      toast.err(
        r.status === 409 ? tt.paketLeer : tt.paketFehlgeschlagen,
        (r.text ?? "").slice(0, 200)
      );
    }
  };

  // Öffnet der Nutzer den „Dokument"-Reiter eines bereits gefüllten Bogens,
  // wird die Vorschau einmal automatisch geladen.
  useEffect(() => {
    if (
      reiter === "dokument" &&
      kopf?.status === "filled" &&
      !vorschauUrl &&
      !vorschauLaedt &&
      !vorschauFehler
    ) {
      void ladeVorschau();
    }
  }, [reiter, kopf?.status, vorschauUrl, vorschauLaedt, vorschauFehler, ladeVorschau]);

  const luecken = useMemo(
    () => (daten?.questions ?? []).filter((q) => q.kind === "gap"),
    [daten]
  );
  const sektionen = useMemo(() => gruppiere(daten?.questions ?? []), [daten]);

  // B1: Folgefragen je Entscheidung — aus den Entscheidungsketten des Matchings.
  const kettenMap = useMemo<Record<string, Frage[]>>(() => {
    const fragen = daten?.questions ?? [];
    const byId = new Map(fragen.map((q) => [q.id, q]));
    const ketten =
      (kopf?.pipeline_state?.matching as { entscheidungsketten?: Kette[] } | undefined)
        ?.entscheidungsketten ?? [];
    const map: Record<string, Frage[]> = {};
    for (const k of ketten) {
      map[k.frage_id] = (k.folgefragen ?? [])
        .map((ff) => byId.get(ff.frage_id))
        .filter(Boolean) as Frage[];
    }
    return map;
  }, [daten, kopf]);

  // B9: wortgleiche Duplikate — jede Zeile bekommt ihren Zwilling (Nummer +
  // ggf. dessen Antwort für die Übernahme).
  const duplikatMap = useMemo<Record<string, { von: number; antwort?: string }>>(() => {
    const nachText = new Map<string, Frage[]>();
    for (const q of daten?.questions ?? []) {
      const key = q.question_text.trim();
      const liste = nachText.get(key);
      if (liste) liste.push(q);
      else nachText.set(key, [q]);
    }
    const map: Record<string, { von: number; antwort?: string }> = {};
    for (const gruppe of nachText.values()) {
      if (gruppe.length < 2) continue;
      const sortiert = [...gruppe].sort((a, b) => a.position - b.position);
      for (const q of sortiert) {
        // Bevorzugt einen Zwilling, der schon eine Antwort trägt (dann lohnt die
        // Übernahme); sonst der nächstliegende andere.
        const zwilling =
          sortiert.find((g) => g.id !== q.id && g.final_answer?.answer) ??
          sortiert.find((g) => g.id !== q.id)!;
        map[q.id] = {
          von: anzeigeNummer(zwilling.position),
          antwort: zwilling.final_answer?.answer,
        };
      }
    }
    return map;
  }, [daten]);

  const deko: EntscheidungDeko = useMemo(
    () => ({
      kontaktVorlagen: daten?.kontakt_vorlagen ?? {},
      verzeichnis,
      kettenMap,
      rollenWerte: rollenWerteRef.current,
      setRollenWerte,
      onEntscheiden: (frage, wahl, gelten) => entscheiden(frage, wahl, gelten),
    }),
    // `entscheiden` schliesst nur über stabile Werte (fragebogenId) — bewusst
    // nicht in den Deps, sonst würde deko bei jedem Render neu gebaut.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [daten, verzeichnis, kettenMap, setRollenWerte]
  );

  if (fehler) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Card padding="none">
          <EmptyState
            icon={<AlertTriangle className="w-6 h-6" />}
            title={t.nichtErreichbar}
            hint={fehler}
            action={
              <Link to="/fragebogen">
                <Button variant="secondary">{t.zurUebersicht}</Button>
              </Link>
            }
          />
        </Card>
      </div>
    );
  }

  if (!kopf || !daten) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10 text-sm text-text-2 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> {t.wirdGeladenLang}
      </div>
    );
  }

  // Der Lauf ist noch unterwegs: die Fragen stehen noch nicht in der Datenbank,
  // und die leere Prüfliste darunter sähe aus wie „nichts gefunden". Stattdessen
  // die Stufe — mit Zähler, sobald das Matching einen meldet.
  if (laeuft) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-3">
        <Link
          to="/fragebogen"
          className="text-xs text-text-2 hover:text-text inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> {t.alleFragebogen}
        </Link>
        <h1 className="font-display text-2xl font-semibold tracking-tight break-words">
          {kopf.filename}
        </h1>
        <Card padding="sm" className="space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Loader2 className="w-4 h-4 animate-spin text-brand-500" />
            {laufBeschriftung(kopf.status, kopf.pipeline_state?.fortschritt)}
          </div>
          <p className="text-xs text-text-2">{t.laufHinweis}</p>
        </Card>
      </div>
    );
  }

  const f = daten.fortschritt;
  const anteil = f.anwendbar ? Math.round((f.reviewt / f.anwendbar) * 100) : 0;
  const info = statusInfo(kopf.status);
  const sichere = daten.questions.filter(
    (q) => q.review_state === "open" && q.confidence === "high" && q.section_applicable
  ).length;

  /**
   * „Geprüft — Dokument ausfüllen": EINE Attestierung statt eines Hakens je
   * Zeile.
   *
   * Die sicheren Antworten stehen in der Liste als Ergebnis da (FrageKarte);
   * dieser Klick ist die Stelle, an der der Prüfer für sie geradesteht. Er
   * übernimmt sie deshalb ZUERST — mit demselben Endpunkt wie der
   * Sammelknopf oben, nur auf Konfidenz „sicher" — und füllt dann.
   *
   * Was hier bewusst NICHT passiert: `alleAbhaken`. Mittlere und unsichere
   * Vorschläge, Lücken und Entscheidungen bleiben offen und lassen den
   * Server-409 stehen. Genau das ist der Punkt — was ein Mensch ansehen muss,
   * darf keine Sammelaktion wegräumen (Konzept §2.2).
   */
  const geprueftUndAusfuellen = async () => {
    if (busy) return;
    if (sichere > 0) {
      setBusy(true);
      try {
        const r = await fragebogenApi.sichereUebernehmen(fragebogenId, ["high"]);
        const tt = katalog().questionnaires;
        toast.ok(tt.vorschlaegeUebernommen(r.uebernommen), tt.attestierungHinweis);
        // Die Liste MUSS hier neu geladen werden, nicht erst nach dem Füllen:
        // bleibt das Füllen gleich darauf am 409 hängen, stünde sonst „N
        // übernommen" über unverändert offenen Zeilen — der teuerste
        // Widerspruch, den diese Seite anbieten kann.
        await ladeFragen();
      } catch (e) {
        toast.err(
          katalog().questionnaires.uebernehmenFehlgeschlagen,
          (e as Error).message
        );
        return;
      } finally {
        setBusy(false);
      }
    }
    await ausfuellen();
  };
  // D-3: Antwortstellen, auf die keine Frage zeigt. Beide Felder sind optional
  // — ein Server ohne das D-3-Paket kennt sie nicht. Fehlt die ZAHL, aber die
  // Liste ist da, zählen wir die Liste: zwei Angaben, die sich widersprechen,
  // wären schlimmer als eine.
  const offeneStellen: OffeneStelle[] = daten.offene_stellen ?? [];
  const offeneStellenZahl = f.offene_stellen ?? offeneStellen.length;
  const abgelehnt = kopf.status === "rejected";
  // Bekannte Fähigkeits-Lücken des Formats (`profiler.py::FormatProfile`).
  // Defensiv gefiltert, weil `format_profile_json` ein gewachsenes JSONB ist:
  // eine alte Zeile kann `unsupported` gar nicht haben, und ein leeres Array
  // darf keinen leeren Kasten erzeugen.
  const einschraenkungen: string[] = Array.isArray(kopf.format_profile?.unsupported)
    ? kopf.format_profile.unsupported.filter(
        (e): e is string => typeof e === "string" && e.trim() !== ""
      )
    : [];

  return (
    // B6: App-Shell — der Header, die Abschnitts-TOC und die Fußleiste bleiben
    // frozen; NUR die Fragenliste scrollt (und stoppt an ihrem Ende). Vorher
    // scrollte das ganze <main>: die TOC wanderte weg und unten lief Leerraum.
    <div className="flex h-full flex-col">
      {/* ── Frozen: Kopf + Fortschritt + Reiter ───────────────────────────── */}
      <div className="flex-none border-b border-border px-4 pb-3 pt-6 sm:px-6">
        <div className="mx-auto max-w-6xl space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <Link
                to="/fragebogen"
                className="inline-flex items-center gap-1 text-xs text-text-2 hover:text-text"
              >
                <ArrowLeft className="w-3 h-3" /> {t.alleFragebogen}
              </Link>
              <h1 className="mt-1 break-words font-display text-2xl font-semibold tracking-tight">
                {kopf.filename}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-text-2">
                <StatusBadge status={info.status}>{info.label}</StatusBadge>
                {kopf.customer && <span>{kopf.customer}</span>}
                {kopf.product_slug && <Chip>{kopf.product_slug}</Chip>}
                {kopf.pipeline_state?.matching?.synthese_modell && (
                  <span className="text-xs text-text-3">
                    {t.vorschlaegeModell(kopf.pipeline_state.matching.synthese_modell)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── D-9: abgelehnter Bogen ────────────────────────────────────── */}
          {abgelehnt && (
            <Card padding="sm" className="border-warning/50 bg-warning-soft/30">
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0 text-warning" />
                <div className="min-w-0">
                  <span className="font-medium">{t.abgelehntTitel}</span>
                  {kopf.error_text && (
                    <p className="mt-1 leading-relaxed text-text-2">{kopf.error_text}</p>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* ── Bekannte Fähigkeits-Lücken des Formats ────────────────────── */}
          {/* Geschwister des Blocks darüber, kein Ersatz: `error_text` gibt es
              nur für einen ABGELEHNTEN Bogen. Diese Meldungen hingegen hängen
              an einem Bogen, der ganz normal weiterläuft (profiler.py:305 und
              :502, beide `verdict=questionnaire`) — genau deshalb erfuhr die
              prüfende Person bisher nie davon. Die Excel-Variante ist der
              teure Fall: die Antworten hinter Formularsteuerelementen tauchen
              gar nicht erst als Frage auf, ihr Fehlen fällt also nicht auf. */}
          {einschraenkungen.length > 0 && (
            <Card padding="sm" className="border-info/40 bg-info-soft/30">
              <div className="flex items-start gap-2 text-sm">
                <Info className="mt-0.5 w-4 h-4 shrink-0 text-info" />
                <div className="min-w-0">
                  <span className="font-medium">{t.einschraenkungenTitel}</span>
                  <p className="mt-1 leading-relaxed text-text-2">
                    {t.einschraenkungenHinweis}
                  </p>
                  {/* Mehrere Einträge sind möglich (selten, aber der Typ lässt
                      es zu) — alle zeigen, nicht nur den ersten. */}
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-text-2">
                    {einschraenkungen.map((meldung) => (
                      <li key={meldung} className="leading-relaxed">
                        {meldung}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          )}

          {/* ── Fortschritt ───────────────────────────────────────────────── */}
          <Card padding="sm" className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <span className="font-medium">{t.geprueftVon(f.reviewt, f.anwendbar)}</span>
                <span className="text-text-2">
                  {" "}
                  · {t.offenUndLuecken(f.offen, f.luecken)}
                  {f.entscheidungen_offen > 0 && t.entscheidungenTeil(f.entscheidungen_offen)}
                  {f.nicht_anwendbar > 0 && t.ausgeklammertTeil(f.nicht_anwendbar)}
                </span>
                {offeneStellenZahl > 0 && (
                  <span className="font-medium text-warning">
                    {t.offeneStellenTeil(offeneStellenZahl)}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<CheckCheck className="w-3.5 h-3.5" />}
                  disabled={busy || sichere === 0}
                  onClick={sichereUebernehmen}
                >
                  {t.sichereUebernehmen(sichere)}
                </Button>
                {/* B8: zweite Sammelaktion — alles Übrige (jeder Konfidenz) abhaken. */}
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<ListChecks className="w-3.5 h-3.5" />}
                  disabled={busy || f.offen === 0}
                  onClick={alleAbhaken}
                  title={t.alleAbhakenTooltip}
                >
                  {t.alleAbhaken}
                </Button>
              </div>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-surface-3">
              <div
                className="h-full bg-success transition-all duration-300"
                style={{ width: `${anteil}%` }}
                role="progressbar"
                aria-valuenow={anteil}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </Card>

          <Tabs
            value={reiter}
            onChange={(id) => setReiter(id as Reiter)}
            items={[
              { id: "review", label: t.reiterFragen, badge: f.anwendbar },
              {
                id: "luecken",
                label: t.reiterLuecken,
                badge: luecken.length + offeneStellen.length,
              },
              {
                id: "anlagen",
                label: t.reiterAnlagen,
                badge: anlagenFragen(daten.questions).length,
              },
              { id: "dokument", label: t.reiterDokument },
            ]}
          />
        </div>
      </div>

      {/* ── Scrollender Körper: NUR die Liste scrollt (B6) ─────────────────── */}
      {/* B6-Fix: `absolute inset-0` gibt dem Bereich eine DEFINITIVE Höhe —
          gegen einen flex-1-Body allein löst `h-full` in Chromium nicht auf, die
          Liste wuchs auf Inhaltshöhe (overflow-y-auto griff nicht) und das
          DOKUMENT wurde scrollbar. Mit definitiver Höhe scrollt nur die Liste. */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {reiter === "review" ? (
          <div className="absolute inset-0 flex justify-center px-0">
          <div className="flex h-full w-full max-w-6xl">
            <div
              ref={listeRef}
              data-fragen-scroller
              className="min-w-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6"
            >
              {/* Auf schmalen Viewports bleibt die Chip-Leiste (B4: Mobile). */}
              <div className="mb-3 lg:hidden">
                <Sprungleiste sektionen={sektionen} scroller={listeRef} />
              </div>
              <div data-fragen-liste className="space-y-4">
                {sektionen.map((s, i) => (
                  <SektionsBlock
                    key={s.name ?? "__ohne__"}
                    id={sektionsId(i)}
                    name={s.name}
                    fragen={s.fragen}
                    // Die Begründung der EXTRAKTION gilt nur, wenn die Extraktion
                    // die Sektion ausgeklammert hat. Sonst stünde unter einer von
                    // Hand ausgeklammerten Sektion der Satz, warum sie zutrifft.
                    grund={
                      (kopf.pipeline_state?.extraction?.sections_not_applicable ?? []).includes(
                        s.name ?? ""
                      )
                        ? (kopf.pipeline_state?.extraction?.section_reasons?.[s.name ?? ""] ??
                          null)
                        : null
                    }
                    kanon={daten.kanon}
                    busy={busy}
                    deko={deko}
                    duplikate={duplikatMap}
                    onSetzen={setzen}
                    onNeuSuchen={neuSuchen}
                    onSektion={sektionSetzen}
                  />
                ))}
              </div>
            </div>
            {/* B4/B6: TOC als eigene, FROZEN Spalte rechts (scrollt nicht mit). */}
            <aside className="hidden w-56 shrink-0 overflow-y-auto border-l border-border px-3 py-4 lg:block">
              <SprungleisteVertikal sektionen={sektionen} scroller={listeRef} />
            </aside>
          </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex justify-center">
          <div className="h-full w-full max-w-6xl overflow-y-auto px-4 py-4 sm:px-6">
            {reiter === "luecken" && (
              <div className="space-y-3">
                <p className="text-sm text-text-2">{t.lueckenHinweis}</p>
                {luecken.length === 0 && (
                  <Card padding="none">
                    <EmptyState title={t.keineLuecken} hint={t.keineLueckenHinweis} />
                  </Card>
                )}
                {luecken.length > 0 && (
                  <div className="overflow-hidden rounded-lg border border-border bg-surface">
                    {luecken.map((q) => (
                      <FrageKarte
                        key={q.id}
                        frage={q}
                        kanon={daten.kanon}
                        busy={busy}
                        onSetzen={(state, antwort) => setzen(q, state, antwort)}
                        onNeuSuchen={() => neuSuchen(q)}
                      />
                    ))}
                  </div>
                )}
                <OffeneStellenBlock stellen={offeneStellen} anzahl={offeneStellenZahl} />
              </div>
            )}
            {reiter === "anlagen" && (
              <AnlagenCheckliste
                fragen={daten.questions}
                registryGroesse={kopf.pipeline_state?.matching?.anlagen_registry ?? 0}
                produkt={kopf.product_slug}
                fragebogenId={fragebogenId}
                onAnlagenGeaendert={ladeFragen}
              />
            )}
            {reiter === "dokument" && (
              <div className="flex h-full flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-text-2">{t.dokumentReiterHinweis}</p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<Stamp className="w-3.5 h-3.5" />}
                      onClick={geprueftUndAusfuellen}
                      disabled={busy || abgelehnt}
                      title={abgelehnt ? t.abgelehntKeinAusfuellen : t.geprueftAusfuellenTooltip}
                    >
                      {vorschauUrl ? t.dokumentNeuFuellen : t.dokumentAusfuellen}
                    </Button>
                    <Button
                      size="sm"
                      icon={<Package className="w-3.5 h-3.5" />}
                      onClick={paketHerunterladen}
                      disabled={busy}
                    >
                      {t.paketDownload}
                    </Button>
                  </div>
                </div>
                {vorschauLaedt ? (
                  <div className="flex items-center gap-2 py-6 text-sm text-text-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> {t.dokumentLaedt}
                  </div>
                ) : vorschauUrl ? (
                  <iframe
                    title={t.reiterDokument}
                    src={vorschauUrl}
                    data-dokument-vorschau
                    className="w-full flex-1 min-h-[70vh] rounded-lg border border-border bg-surface"
                  />
                ) : (
                  <Card padding="none">
                    <EmptyState
                      icon={<FileText className="w-6 h-6" />}
                      title={vorschauFehler ? t.dokumentFehlerTitel : t.dokumentLeerTitel}
                      hint={vorschauFehler ?? t.dokumentLeerHinweis}
                      action={
                        <Button
                          variant="secondary"
                          onClick={geprueftUndAusfuellen}
                          disabled={busy || abgelehnt}
                        >
                          {t.dokumentAusfuellen}
                        </Button>
                      }
                    />
                  </Card>
                )}
              </div>
            )}
          </div>
          </div>
        )}
      </div>

      {/* ── Frozen Fußleiste ──────────────────────────────────────────────── */}
      <div className="flex-none border-t border-border bg-surface px-4 py-3">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
          <span
            className={clsx("text-xs", abgelehnt ? "font-medium text-warning" : "text-text-2")}
          >
            {abgelehnt ? t.abgelehntZeile : abschlussText(t, f, offeneStellenZahl)}
          </span>
          <div className="flex items-center gap-2">
            <Button
              icon={<Stamp className="w-4 h-4" />}
              onClick={geprueftUndAusfuellen}
              disabled={busy || abgelehnt}
              title={abgelehnt ? t.abgelehntKeinAusfuellen : t.geprueftAusfuellenTooltip}
            >
              {t.geprueftAusfuellen}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Der Satz in der Abschluss-Leiste.
 *
 * Die eine Regel, die er einhalten muss (T9 D-3): **„Alles bearbeitet" darf
 * nicht dastehen, solange es Antwortzellen gibt, auf die keine Frage zeigt.**
 * Der Prüfer kann nur prüfen, was ihm gezeigt wird; ein „alles" über 12 stillen
 * Leerzellen ist die gefährlichste Sorte Halbwahrheit.
 *
 * Bewusst NICHT „noch N von M zu prüfen": die Lücken zählen dort mit und lasen
 * sich wie unerledigte Arbeit, obwohl sie das Ergebnis sind (Konzept §3).
 */
function abschlussText(
  t: Katalog["questionnaires"],
  f: Fortschritt,
  offeneStellen: number
): string {
  if (f.offen === 0 && f.entscheidungen_offen === 0) {
    return offeneStellen > 0
      ? t.bearbeitetMitOffenenStellen(f.reviewt, f.luecken, offeneStellen)
      : t.allesBearbeitet(f.reviewt, f.luecken);
  }
  return (
    t.nochOffen(f.offen) +
    (f.entscheidungen_offen ? t.entscheidungenTeil(f.entscheidungen_offen) : "") +
    (f.luecken ? t.lueckenTeil(f.luecken) : "") +
    (offeneStellen ? t.offeneStellenTeil(offeneStellen) : "")
  );
}

// ── D-3: Zellen ohne Frage ──────────────────────────────────────────────────

/**
 * Die Antwortstellen des Originaldokuments, auf die keine extrahierte Frage
 * zeigt — im Reiter „Lücken", weil das dieselbe Sorte Arbeit ist: etwas, das
 * die Maschine nicht kann und ein Mensch übernehmen muss. Der Unterschied zur
 * Lückenkarte steht im Erklärungssatz: dort fehlt die ANTWORT, hier fehlt die
 * FRAGE.
 */
function OffeneStellenBlock({
  stellen,
  anzahl,
}: {
  stellen: OffeneStelle[];
  anzahl: number;
}) {
  const t = useT().questionnaires;
  if (anzahl === 0) return null;
  // "xlsx-bereits-ausgefuellt" (Haertung 10) ist keine unbearbeitete Stelle —
  // die Gegenseite hat das Feld schon beantwortet, bitop schuldet nichts.
  // Eigener, nicht-warnender Block statt der "muss von Hand bearbeitet
  // werden"-Karte unten, sonst behauptet die Oberflaeche selbst wieder eine
  // Luecke, die keine ist.
  const beantwortet = stellen.filter((s) => s.art === "xlsx-bereits-ausgefuellt");
  const unbearbeitet = stellen.filter((s) => s.art !== "xlsx-bereits-ausgefuellt");
  const unbearbeitetAnzahl = anzahl - beantwortet.length;
  // Lange Listen schneiden ab: 30 Einträge reichen, um die Sorte zu erkennen,
  // und die Zahl in der Überschrift bleibt vollständig.
  const gezeigt = unbearbeitet.slice(0, 30);
  const rest = unbearbeitetAnzahl - gezeigt.length;
  return (
    <>
      {beantwortet.length > 0 && (
        <Card padding="sm" className="border-border bg-surface-2 space-y-2">
          <div className="text-sm font-medium">
            {t.bereitsAusgefuelltTitel(beantwortet.length)}
          </div>
          <p className="text-xs text-text-2 leading-relaxed">
            {t.bereitsAusgefuelltErklaerung}
          </p>
          <ul className="text-xs text-text-2 space-y-1">
            {beantwortet.map((s, i) => (
              <li key={`${s.art}-${s.bezeichnung}-${i}`} className="flex items-start gap-2">
                <Chip>{ankerArt(s.art)}</Chip>
                <span className="min-w-0 break-words">{s.bezeichnung}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {unbearbeitetAnzahl > 0 && (
        <Card padding="sm" className="border-warning/50 bg-warning-soft/20 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
            {t.offeneStellenTitel(unbearbeitetAnzahl)}
          </div>
          <p className="text-xs text-text-2 leading-relaxed">{t.offeneStellenErklaerung}</p>
          {gezeigt.length > 0 && (
            <ul className="text-xs text-text-2 space-y-1">
              {gezeigt.map((s, i) => (
                <li key={`${s.art}-${s.bezeichnung}-${i}`} className="flex items-start gap-2">
                  <Chip>{ankerArt(s.art)}</Chip>
                  <span className="min-w-0 break-words">{s.bezeichnung}</span>
                </li>
              ))}
            </ul>
          )}
          {gezeigt.length > 0 && rest > 0 && (
            <p className="text-xs text-text-3">{t.offeneStellenWeitere(rest)}</p>
          )}
        </Card>
      )}
    </>
  );
}

// ── Sektionen ───────────────────────────────────────────────────────────────

interface Sektion {
  name: string | null;
  fragen: Frage[];
}

function gruppiere(fragen: Frage[]): Sektion[] {
  const map = new Map<string, Sektion>();
  for (const f of fragen) {
    const schluessel = f.section ?? "__ohne__";
    if (!map.has(schluessel)) map.set(schluessel, { name: f.section, fragen: [] });
    map.get(schluessel)!.fragen.push(f);
  }
  return [...map.values()];
}

/** Sprungziel-Id über den INDEX und nicht über den Namen: Abschnittsnamen
 *  kommen aus dem Kundendokument und enthalten Leerzeichen, Punkte und
 *  Sonderzeichen — als DOM-Id wären sie eine Fehlerquelle ohne Gegenwert. */
const sektionsId = (i: number) => `abschnitt-${i}`;

function geprueftIn(fragen: Frage[]): number {
  return fragen.filter(
    (f) => f.review_state === "accepted" || f.review_state === "edited"
  ).length;
}

/**
 * Scrollt AUSSCHLIESSLICH den Listen-Scroller zu einem Abschnitt (B6-Fix).
 *
 * `element.scrollIntoView()` bewegt JEDEN scrollbaren Vorfahren bis zum
 * `<html>` — und genau das war der „die ganze Seite scrollt"-Bug: das Dokument
 * scrollte mit. Hier wird nur der übergebene Scroller per `scrollBy` um die
 * Differenz seiner Oberkante zur Abschnitts-Oberkante bewegt. Ohne Scroller-Ref
 * (Sonderfall) bleibt der alte Weg.
 */
function scrolleZuSektion(scroller: HTMLElement | null, id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  if (scroller) {
    const delta = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    scroller.scrollBy({ top: delta - 8, behavior: "smooth" });
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

/**
 * Die Sprungleiste: alle Abschnitte mit `geprüft/gesamt`, der aktive
 * hervorgehoben, Klick scrollt hin.
 *
 * Sie ist der Ersatz für das, was der Prüfer am Papier-Fragebogen umsonst hat:
 * zu sehen, wo er ist. Der aktive Abschnitt wird aus den Positionen gemessen
 * und nicht mitgeschrieben — der Nutzer scrollt auch mit dem Rad, nicht nur
 * über diese Leiste.
 */
function Sprungleiste({
  sektionen,
  scroller,
}: {
  sektionen: Sektion[];
  scroller: RefObject<HTMLDivElement>;
}) {
  const t = useT().questionnaires;
  const leiste = useRef<HTMLDivElement>(null);
  const [aktiv, setAktiv] = useState(0);

  useEffect(() => {
    const messen = () => {
      // Unterkante der Leiste + Luft: nach einem Sprung liegt der Kopf des
      // Ziel-Abschnitts direkt darunter (`scroll-mt-14`) und muss noch als
      // „aktiv" zählen.
      const grenze = (leiste.current?.getBoundingClientRect().bottom ?? 40) + 64;
      let neu = 0;
      sektionen.forEach((_, i) => {
        const el = document.getElementById(sektionsId(i));
        if (el && el.getBoundingClientRect().top <= grenze) neu = i;
      });
      setAktiv(neu);
    };
    messen();
    // Der echte Scroller ist `<main>` (components/Layout.tsx:645), nicht das
    // Fenster. `scroll` steigt nicht auf — deshalb in der EINFANG-Phase, dann
    // ist es egal, welches Element tatsächlich scrollt.
    window.addEventListener("scroll", messen, true);
    window.addEventListener("resize", messen);
    return () => {
      window.removeEventListener("scroll", messen, true);
      window.removeEventListener("resize", messen);
    };
  }, [sektionen]);

  if (sektionen.length < 2) return null;

  return (
    <div
      ref={leiste}
      data-sprungleiste
      aria-label={t.sprungleisteTitel}
      className="sticky top-0 z-20 -mx-1 flex gap-1 overflow-x-auto border-b border-border bg-surface/95 px-1 py-1.5 backdrop-blur"
    >
      {sektionen.map((s, i) => {
        const name = s.name ?? t.ohneSektion;
        const geprueft = geprueftIn(s.fragen);
        return (
          <button
            key={sektionsId(i)}
            type="button"
            data-sprung-ziel={sektionsId(i)}
            aria-current={i === aktiv ? "true" : undefined}
            title={t.sprungZiel(name, geprueft, s.fragen.length)}
            onClick={() => scrolleZuSektion(scroller.current, sektionsId(i))}
            className={clsx(
              "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
              i === aktiv
                ? "bg-accent-500 text-white"
                : "bg-surface-3 text-text-2 hover:bg-surface-2"
            )}
          >
            {/* Lange Abschnittsnamen werden gekürzt — der volle steht im
                title-Attribut, damit die Leiste eine Zeile bleibt. */}
            <span className="max-w-[8rem] truncate">{name}</span>
            <span className="tabular-nums opacity-80">
              {geprueft}/{s.fragen.length}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SektionsBlock({
  id,
  name,
  fragen,
  grund,
  kanon,
  busy,
  deko,
  duplikate,
  onSetzen,
  onNeuSuchen,
  onSektion,
}: {
  id: string;
  name: string | null;
  fragen: Frage[];
  grund: string | null;
  kanon: Record<string, string>;
  busy: boolean;
  deko: EntscheidungDeko;
  duplikate: Record<string, { von: number; antwort?: string }>;
  onSetzen: (f: Frage, s: ReviewState, a?: FinaleAntwort | null) => void;
  onNeuSuchen: (f: Frage) => void;
  onSektion: (name: string | null, applicable: boolean) => void;
}) {
  const t = useT().questionnaires;
  // Der Geltungsbereich hängt an den Fragen, nicht an einer Sektions-Tabelle
  // (es gibt keine) — eine Sektion gilt, wenn ihre Fragen gelten.
  const gilt = fragen.some((f) => f.section_applicable);
  const [offen, setOffen] = useState(gilt);
  const geprueft = geprueftIn(fragen);

  return (
    <section
      id={id}
      // Der Sprung landet unter der Sprungleiste und nicht darunter versteckt.
      className="scroll-mt-14"
      data-sektion={name ?? "(ohne)"}
      data-gilt={gilt ? "1" : "0"}
    >
      <div
        className={clsx(
          "flex items-center justify-between gap-3 flex-wrap py-1.5 border-b",
          gilt ? "border-border" : "border-border-strong bg-surface-3/50 px-2 rounded-t-md"
        )}
      >
        <button
          onClick={() => setOffen((o) => !o)}
          className="text-sm font-semibold text-text inline-flex items-center gap-2"
          aria-expanded={offen}
        >
          {name ?? t.ohneSektion}
          <span className="text-xs font-normal text-text-3">
            {t.sektionGeprueft(geprueft, fragen.length)}
          </span>
          {!gilt && <StatusBadge status="neutral">{t.giltNicht}</StatusBadge>}
        </button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            onSektion(name, !gilt);
            setOffen(!gilt);
          }}
        >
          {gilt ? t.giltNichtSetzen : t.wiederAufnehmen}
        </Button>
      </div>

      {!gilt && (
        <p className="text-xs text-text-2 px-2 py-2">
          {grund ? t.sektionAusgeklammert(grund) : t.sektionVonHand}
        </p>
      )}

      {offen && (
        <div className="mt-2 overflow-hidden rounded-lg border border-border bg-surface">
          {fragen.map((f) => {
            const d = duplikate[f.id];
            return (
              <FrageKarte
                key={f.id}
                frage={f}
                kanon={kanon}
                busy={busy}
                deko={deko}
                duplikatVon={d?.von}
                onDuplikatUebernehmen={
                  d?.antwort
                    ? () => onSetzen(f, "edited", { answer: d.antwort })
                    : undefined
                }
                onSetzen={(s, a) => onSetzen(f, s, a)}
                onNeuSuchen={() => onNeuSuchen(f)}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}


/**
 * Die mitlaufende Abschnitts-TOC RECHTS (B4, Desktop).
 *
 * Ersetzt die horizontale Chip-Leiste, die bei 17 Abschnitten überlief (ab „E."
 * war auf 1440 px Schluss). „Platz ist ja da" (WH): rechts steht die volle
 * Liste, der aktive Abschnitt ist hervorgehoben, geprüft/gesamt je Abschnitt.
 * Der aktive Abschnitt wird aus den Positionen gemessen (der Nutzer scrollt auch
 * mit dem Rad), Grenze fest bei 120 px unter der Viewport-Oberkante.
 */
function SprungleisteVertikal({
  sektionen,
  scroller,
}: {
  sektionen: Sektion[];
  scroller: RefObject<HTMLDivElement>;
}) {
  const t = useT().questionnaires;
  const [aktiv, setAktiv] = useState(0);

  useEffect(() => {
    const messen = () => {
      // Grenze = Oberkante des Listen-Scrollers + Luft: der Abschnitt, dessen
      // Kopf gerade unter die Scroller-Kante gewandert ist, gilt als aktiv.
      const grenze = (scroller.current?.getBoundingClientRect().top ?? 0) + 12;
      let neu = 0;
      sektionen.forEach((_, i) => {
        const el = document.getElementById(sektionsId(i));
        if (el && el.getBoundingClientRect().top <= grenze) neu = i;
      });
      setAktiv(neu);
    };
    messen();
    // Der Scroller ist die Fragenliste — `scroll` steigt nicht auf, deshalb in
    // der EINFANG-Phase (fängt auch das Scrollen des Listen-Div ab).
    window.addEventListener("scroll", messen, true);
    window.addEventListener("resize", messen);
    return () => {
      window.removeEventListener("scroll", messen, true);
      window.removeEventListener("resize", messen);
    };
  }, [sektionen, scroller]);

  if (sektionen.length < 2) return null;

  return (
    <nav
      data-sprungleiste-vertikal
      aria-label={t.sprungleisteTitel}
      className="space-y-0.5 text-sm"
    >
      {sektionen.map((s, i) => {
        const name = s.name ?? t.ohneSektion;
        const geprueft = geprueftIn(s.fragen);
        const fertig = s.fragen.length > 0 && geprueft === s.fragen.length;
        return (
          <button
            key={sektionsId(i)}
            type="button"
            data-sprung-ziel={sektionsId(i)}
            aria-current={i === aktiv ? "true" : undefined}
            title={t.sprungZiel(name, geprueft, s.fragen.length)}
            onClick={() => scrolleZuSektion(scroller.current, sektionsId(i))}
            className={clsx(
              "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1 text-left transition-colors",
              i === aktiv
                ? "bg-accent-50 font-medium text-accent-700"
                : "text-text-2 hover:bg-surface-3"
            )}
          >
            <span className="truncate">{name}</span>
            <span
              className={clsx(
                "shrink-0 text-xs tabular-nums",
                fertig ? "text-success" : "text-text-3"
              )}
            >
              {geprueft}/{s.fragen.length}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

interface Kette {
  frage_id: string;
  folgefragen?: { frage_id: string; position: number }[];
}
